import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Shield, Users, FileText, ShoppingBag, DollarSign,
  Crown, Ban, Trash2, Loader2, ChevronDown, ChevronUp,
  BarChart3, KeyRound
} from "lucide-react";
import api from "../lib/api";

export default function AdminPanel() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminSecret, setAdminSecret] = useState("");
  const [promoting, setPromoting] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("vita_user");
    if (!stored) { navigate("/auth"); return; }
    const u = JSON.parse(stored);
    setUser(u);
    setIsAdmin(u.role === "admin");
    if (u.role === "admin") loadDashboard();
  }, [navigate]);

  const loadDashboard = async () => {
    try {
      const res = await api.get("/admin/dashboard");
      setDashboard(res.data);
    } catch { toast.error("Failed to load admin dashboard"); }
  };

  const promote = async () => {
    if (!adminSecret) return;
    setPromoting(true);
    try {
      await api.post("/admin/promote", { admin_secret: adminSecret });
      const me = await api.get("/auth/me");
      localStorage.setItem("vita_user", JSON.stringify(me.data));
      setUser(me.data);
      setIsAdmin(true);
      loadDashboard();
      toast.success("Admin access granted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid admin secret");
    } finally { setPromoting(false); }
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/admin/users"); setUsers(r.data.users); } catch (err) { console.error('Error:', err.message || err); } finally { setLoading(false); }
  }, []);
  const loadPosts = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/admin/posts"); setPosts(r.data.posts); } catch (err) { console.error('Error:', err.message || err); } finally { setLoading(false); }
  }, []);
  const loadProducts = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/admin/products"); setProducts(r.data.products); } catch (err) { console.error('Error:', err.message || err); } finally { setLoading(false); }
  }, []);
  const loadTransactions = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/admin/transactions"); setTransactions(r.data.transactions); } catch (err) { console.error('Error:', err.message || err); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    if (activeTab === "users") loadUsers();
    if (activeTab === "posts") loadPosts();
    if (activeTab === "products") loadProducts();
    if (activeTab === "transactions") loadTransactions();
  }, [activeTab, isAdmin, loadUsers, loadPosts, loadProducts, loadTransactions]);

  const updateTier = async (userId, tier) => {
    try {
      await api.put(`/admin/users/${userId}/tier`, { tier });
      toast.success("Tier updated");
      loadUsers();
      loadDashboard();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const toggleBan = async (userId, banned) => {
    try {
      await api.put(`/admin/users/${userId}/ban`, { banned });
      toast.success(banned ? "User banned" : "User unbanned");
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const deletePost = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/admin/posts/${postId}`); toast.success("Post deleted"); loadPosts(); loadDashboard(); }
    catch { toast.error("Failed"); }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/admin/products/${productId}`); toast.success("Product deleted"); loadProducts(); loadDashboard(); }
    catch { toast.error("Failed"); }
  };

  if (!user) return null;

  // Login gate
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-transparent" data-testid="admin-login-page">
        <Navbar />
        <div className="max-w-md mx-auto px-6 py-24">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 text-center">
            <div className="w-16 h-16 bg-[#F2F9F6] rounded-full flex items-center justify-center mx-auto mb-6">
              <KeyRound className="h-8 w-8 text-[#0B6E4F]" />
            </div>
            <h2 className="font-['Outfit'] text-2xl font-bold text-[#0F172A] mb-2">Admin Access</h2>
            <p className="text-sm text-slate-500 mb-6">Enter the admin secret key to unlock the admin panel.</p>
            <input
              type="password"
              value={adminSecret}
              onChange={e => setAdminSecret(e.target.value)}
              placeholder="Admin secret key"
              data-testid="admin-secret-input"
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0B6E4F] outline-none mb-4"
              onKeyDown={e => e.key === "Enter" && promote()}
            />
            <Button onClick={promote} disabled={promoting} data-testid="admin-promote-btn" className="w-full bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full font-semibold">
              {promoting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />} Unlock Admin Panel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "users", label: "Users", icon: Users },
    { id: "posts", label: "Posts", icon: FileText },
    { id: "products", label: "Products", icon: ShoppingBag },
    { id: "transactions", label: "Transactions", icon: DollarSign },
  ];

  const tierColors = {
    free: "bg-slate-100 text-slate-600", free_trial: "bg-amber-50 text-amber-700",
    premium: "bg-emerald-50 text-emerald-700", super_premium: "bg-blue-50 text-blue-700",
  };

  return (
    <div className="min-h-screen bg-transparent" data-testid="admin-panel">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="h-7 w-7 text-[#0B6E4F]" />
          <h1 className="font-['Outfit'] text-3xl font-bold text-[#0F172A]">Admin Panel</h1>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${activeTab === t.id ? "bg-[#0B6E4F] text-white shadow-lg" : "bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Overview */}
        {activeTab === "overview" && dashboard && (
          <div className="space-y-6" data-testid="admin-overview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Users", value: dashboard.stats.total_users, color: "bg-blue-50 text-blue-700", icon: Users },
                { label: "Posts", value: dashboard.stats.total_posts, color: "bg-green-50 text-green-700", icon: FileText },
                { label: "Products", value: dashboard.stats.total_products, color: "bg-purple-50 text-purple-700", icon: ShoppingBag },
                { label: "Revenue", value: `$${dashboard.revenue.total}`, color: "bg-amber-50 text-amber-700", icon: DollarSign },
              ].map(s => (
                <div key={s.label} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
                  <div className={`w-10 h-10 ${s.color} rounded-xl flex items-center justify-center mb-3`}>
                    <s.icon className="h-5 w-5" />
                  </div>
                  <p className="text-2xl font-bold text-[#0F172A]">{s.value}</p>
                  <p className="text-xs text-slate-500 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <h3 className="font-semibold text-[#0F172A] mb-4">Subscription Breakdown</h3>
                {Object.entries(dashboard.subscriptions).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${tierColors[k] || "bg-slate-100"}`}>{k.replace("_", " ")}</span>
                    <span className="font-semibold text-[#0F172A]">{v}</span>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <h3 className="font-semibold text-[#0F172A] mb-4">Revenue Breakdown</h3>
                {[
                  { label: "Subscriptions", value: dashboard.revenue.subscriptions },
                  { label: "Marketplace", value: dashboard.revenue.marketplace },
                  { label: "Commission (10%)", value: dashboard.revenue.commission },
                ].map(r => (
                  <div key={r.label} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                    <span className="text-sm text-slate-600">{r.label}</span>
                    <span className="font-semibold text-[#0F172A]">${r.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="admin-users-tab">
            {loading ? <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50"><tr>
                    <th className="text-left p-4 font-medium text-slate-500">User</th>
                    <th className="text-left p-4 font-medium text-slate-500">Email</th>
                    <th className="text-left p-4 font-medium text-slate-500">Tier</th>
                    <th className="text-left p-4 font-medium text-slate-500">Joined</th>
                    <th className="text-left p-4 font-medium text-slate-500">Actions</th>
                  </tr></thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.user_id} className={`border-t border-slate-50 ${u.banned ? "bg-red-50/50" : ""}`}>
                        <td className="p-4 font-medium text-[#0F172A]">{u.name}{u.role === "admin" && <span className="ml-2 text-xs bg-[#0B6E4F] text-white px-2 py-0.5 rounded-full">admin</span>}{u.banned && <span className="ml-2 text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">banned</span>}</td>
                        <td className="p-4 text-slate-500">{u.email}</td>
                        <td className="p-4"><span className={`px-3 py-1 rounded-full text-xs font-medium ${tierColors[u.subscription_tier] || "bg-slate-100"}`}>{u.subscription_tier?.replace("_", " ")}</span></td>
                        <td className="p-4 text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}</td>
                        <td className="p-4">
                          <div className="flex gap-1">
                            <select value={u.subscription_tier} onChange={e => updateTier(u.user_id, e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white" data-testid={`tier-select-${u.user_id}`}>
                              <option value="free">Free</option>
                              <option value="free_trial">Free Trial</option>
                              <option value="premium">Premium</option>
                              <option value="super_premium">Super Premium</option>
                            </select>
                            <Button size="sm" variant={u.banned ? "default" : "destructive"} onClick={() => toggleBan(u.user_id, !u.banned)} className="text-xs h-7 px-2 rounded-lg">
                              <Ban className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Posts Tab */}
        {activeTab === "posts" && (
          <div className="space-y-3" data-testid="admin-posts-tab">
            {loading ? <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div> :
              posts.length === 0 ? <p className="text-slate-500 text-center py-12">No posts yet</p> :
              posts.map(p => (
                <div key={p.post_id} className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm text-[#0F172A]">{p.author_name}</span>
                      <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{p.community_type}</span>
                    </div>
                    <p className="text-sm text-slate-600 truncate">{p.content}</p>
                    {p.image_url && <img src={p.image_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${p.image_url}` : p.image_url} alt="" className="w-20 h-14 object-cover rounded-lg mt-2" />}
                  </div>
                  <Button size="sm" variant="destructive" onClick={() => deletePost(p.post_id)} className="shrink-0 h-8 px-3 rounded-lg" data-testid={`delete-post-${p.post_id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))
            }
          </div>
        )}

        {/* Products Tab */}
        {activeTab === "products" && (
          <div className="space-y-3" data-testid="admin-products-tab">
            {loading ? <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div> :
              products.length === 0 ? <p className="text-slate-500 text-center py-12">No products yet</p> :
              products.map(p => (
                <div key={p.product_id} className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    {p.image_url && <img src={p.image_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${p.image_url}` : p.image_url} alt="" className="w-14 h-14 object-cover rounded-lg flex-shrink-0" />}
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-[#0F172A] truncate">{p.title}</p>
                      <p className="text-xs text-slate-500">${p.price} - {p.category}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="destructive" onClick={() => deleteProduct(p.product_id)} className="shrink-0 h-8 px-3 rounded-lg" data-testid={`delete-product-${p.product_id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))
            }
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === "transactions" && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="admin-transactions-tab">
            {loading ? <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50"><tr>
                    <th className="text-left p-4 font-medium text-slate-500">ID</th>
                    <th className="text-left p-4 font-medium text-slate-500">Amount</th>
                    <th className="text-left p-4 font-medium text-slate-500">Type</th>
                    <th className="text-left p-4 font-medium text-slate-500">Status</th>
                    <th className="text-left p-4 font-medium text-slate-500">Date</th>
                  </tr></thead>
                  <tbody>
                    {transactions.map(t => (
                      <tr key={t.transaction_id} className="border-t border-slate-50">
                        <td className="p-4 text-slate-600 font-mono text-xs">{t.transaction_id}</td>
                        <td className="p-4 font-medium">${t.amount}</td>
                        <td className="p-4"><span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{t.plan_type || t.metadata?.type || "payment"}</span></td>
                        <td className="p-4"><span className={`text-xs px-2 py-0.5 rounded-full ${t.payment_status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{t.payment_status}</span></td>
                        <td className="p-4 text-slate-500">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {transactions.length === 0 && <p className="text-slate-500 text-center py-12">No transactions yet</p>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
